/**
 * Add a whole category (site) at runtime, with its key lifecycle.
 *
 * A category is more than a host: it carries the health path, the header its keys go
 * in, the hosts its key is trusted with, and - the reason this form is more than a text
 * box - the *issue* and *verify* endpoints that let detailed mode mint and check a key.
 * Those live behind a disclosure because most of the form is the simple case (a name
 * and a host) and the lifecycle is the expert part.
 *
 * The payload fields are entered as JSON and validated here before submit, so a typo is
 * caught with a pointer to the field rather than a 422 from the server. No secret is
 * entered here: the header *value* still comes from the environment on the server - this
 * form supplies the shape, not the credential.
 */

import { ChevronDown, FolderPlus } from 'lucide-react'
import { useState } from 'react'

import type { ApiKeyEndpointConfig, NewCategoryRequest } from '../api/types'
import { cn } from '../lib/cn'
import {
  EMPTY_ENDPOINT,
  EndpointFieldset,
  buildEndpoint,
  inputClass,
} from './ui/EndpointFields'
import type { EndpointFields } from './ui/EndpointFields'

interface AddCategoryFormProps {
  onSubmit: (body: NewCategoryRequest) => Promise<unknown>
  isPending: boolean
  error: string | null
  existingIds: string[]
}

function slugify(value: string): string {
  return value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
}

export function AddCategoryForm({
  onSubmit,
  isPending,
  error,
  existingIds,
}: AddCategoryFormProps) {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [id, setId] = useState('')
  const [path, setPath] = useState('/api/health')
  const [header, setHeader] = useState('X-API-Key')
  const [suffixes, setSuffixes] = useState('')
  const [host, setHost] = useState('')
  const [scheme, setScheme] = useState<'http' | 'https'>('https')
  const [showLifecycle, setShowLifecycle] = useState(false)
  const [issue, setIssue] = useState<EndpointFields>(EMPTY_ENDPOINT)
  const [verify, setVerify] = useState<EndpointFields>({ ...EMPTY_ENDPOINT, method: 'GET' })
  const [localError, setLocalError] = useState<string | null>(null)

  // The id defaults to a slug of the name until the operator types their own.
  const [idTouched, setIdTouched] = useState(false)
  const effectiveId = idTouched ? id : slugify(name)

  const reset = () => {
    setName(''); setId(''); setIdTouched(false); setHost('')
    setSuffixes(''); setIssue(EMPTY_ENDPOINT)
    setVerify({ ...EMPTY_ENDPOINT, method: 'GET' }); setShowLifecycle(false)
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setLocalError(null)

    const finalId = slugify(effectiveId)
    if (!finalId) return setLocalError('A category needs a name or id.')
    if (existingIds.includes(finalId)) {
      return setLocalError(`A category with id "${finalId}" already exists.`)
    }
    if (!host.trim()) return setLocalError('A category needs at least one host.')

    let issueConfig: ApiKeyEndpointConfig | null
    let verifyConfig: ApiKeyEndpointConfig | null
    try {
      issueConfig = buildEndpoint(issue, 'issue')
      verifyConfig = buildEndpoint(verify, 'verify')
    } catch (err) {
      return setLocalError((err as Error).message)
    }

    const body: NewCategoryRequest = {
      id: finalId,
      name: name.trim() || finalId,
      path: path.trim() || '/api/health',
      api_key_header: header.trim() || 'X-API-Key',
      trusted_host_suffixes: suffixes
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
      environments: [
        { id: 'prod', label: 'Production', host: host.trim().replace(/^https?:\/\//, ''), scheme },
      ],
      ...(issueConfig ? { issue_api_key: issueConfig } : {}),
      ...(verifyConfig ? { verify_api_key: verifyConfig } : {}),
    }

    try {
      await onSubmit(body)
      reset()
    } catch {
      /* The server-side error surfaces via the `error` prop. */
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="card flex w-full items-center justify-center gap-2 p-3 text-sm text-ink-secondary transition-colors hover:bg-surface-sunken"
      >
        <FolderPlus size={15} aria-hidden />
        Add a category
      </button>
    )
  }

  return (
    <section className="card p-4" aria-label="Add a category">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-ink">Add a category</h2>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-ink-secondary hover:text-ink"
        >
          Cancel
        </button>
      </div>
      <p className="mt-1 text-xs text-ink-secondary">
        A new product to monitor, with its own hosts and — optionally — the endpoints that
        issue and verify its API keys. The key’s header value comes from the environment
        on the server; nothing here is a secret.
      </p>

      <form onSubmit={submit} className="mt-3 space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="New Service"
              className={inputClass}
            />
          </Field>
          <Field label="Id" hint="Used in URLs and env-var names.">
            <input
              value={effectiveId}
              onChange={(e) => {
                setIdTouched(true)
                setId(e.target.value)
              }}
              placeholder="new_service"
              className={inputClass}
            />
          </Field>
          <Field label="Health path">
            <input value={path} onChange={(e) => setPath(e.target.value)} className={inputClass} />
          </Field>
          <Field label="API key header">
            <input value={header} onChange={(e) => setHeader(e.target.value)} className={inputClass} />
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-[8rem_minmax(0,1fr)]">
          <Field label="Scheme">
            <select
              value={scheme}
              onChange={(e) => setScheme(e.target.value as 'http' | 'https')}
              className={inputClass}
            >
              <option value="https">https</option>
              <option value="http">http</option>
            </select>
          </Field>
          <Field label="Host" hint="The first environment. Add more later in sites.json.">
            <input
              value={host}
              onChange={(e) => setHost(e.target.value)}
              placeholder="service.whitehawk.io"
              className={inputClass}
            />
          </Field>
        </div>

        <Field
          label="Trusted host suffixes"
          hint="Comma-separated. A key is only ever sent to a host matching one of these."
        >
          <input
            value={suffixes}
            onChange={(e) => setSuffixes(e.target.value)}
            placeholder="whitehawk.io"
            className={inputClass}
          />
        </Field>

        {/* The lifecycle is the expert part - hidden until asked for. */}
        <button
          type="button"
          onClick={() => setShowLifecycle((v) => !v)}
          aria-expanded={showLifecycle}
          className="inline-flex items-center gap-1 text-xs text-ink-secondary hover:text-ink"
        >
          <ChevronDown
            size={13}
            aria-hidden
            className={cn('transition-transform', showLifecycle && 'rotate-180')}
          />
          API key lifecycle (for detailed mode)
        </button>

        {showLifecycle ? (
          <div className="space-y-3 rounded-md border p-3">
            <p className="text-[11px] text-ink-muted">
              Leave blank if this category has no keys. Detailed mode will issue a key
              when a host has none, and verify a stored one before use. Paths are joined
              onto the target host. A payload may contain{' '}
              <code className="text-ink-secondary">{'{api_key}'}</code>, substituted at
              call time.
            </p>
            <EndpointFieldset
              title="Issue endpoint"
              fields={issue}
              onChange={setIssue}
              showKeyField
            />
            <EndpointFieldset title="Verify endpoint" fields={verify} onChange={setVerify} />
          </div>
        ) : null}

        {localError || error ? (
          <p className="text-xs text-critical">{localError ?? error}</p>
        ) : null}

        <button
          type="submit"
          disabled={isPending}
          className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium text-ink transition-colors hover:bg-surface-sunken disabled:opacity-60"
        >
          <FolderPlus size={13} aria-hidden />
          {isPending ? 'Adding…' : 'Add category'}
        </button>
      </form>
    </section>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] text-ink-muted">{label}</span>
      {children}
      {hint ? <span className="text-[10px] text-ink-muted">{hint}</span> : null}
    </label>
  )
}
